import React from "react";
import { useTranslation } from "react-i18next";
import { FileText, Plus, Edit2, Trash2, Check, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import Dialog from "@/components/common/Dialog";
import TrashCan from "@/assets/svg/TrashCan.svg";
import ErrorMessage from "@/components/common/ErrorMessage";
import dateFormater from "@/helperFuntions/dateFormater";

export interface TaskNoteItem {
  noteId: string;
  note: string;
  createdAt: string | Date;
  createdByUser?: {
    firstName?: string;
    lastName?: string;
  };
  updatedByUser?: {
    firstName?: string;
    lastName?: string;
  };
}

interface TaskNotesSectionProps {
  notes?: TaskNoteItem[];
  onAddNote: (note: string) => void;
  onUpdateNote: (noteId: string, note: string) => void;
  onDeleteNote: (noteId: string) => void;
  allowed: boolean;
  isAddingNote?: boolean;
}

export const TaskNotesSection: React.FC<TaskNotesSectionProps> = ({
  notes = [],
  onAddNote,
  onUpdateNote,
  onDeleteNote,
  allowed,
}) => {
  const { t } = useTranslation();
  const [showAddBox, setShowAddBox] = React.useState(false);
  const [newNoteText, setNewNoteText] = React.useState("");
  const [editingNoteId, setEditingNoteId] = React.useState<string | null>(null);
  const [editingNoteText, setEditingNoteText] = React.useState("");
  const [addError, setAddError] = React.useState("");
  const [deleteNoteId, setDeleteNoteId] = React.useState<string | null>(null);

  const handleAddSubmit = () => {
    if (!newNoteText.trim()) {
      setAddError(t("please_enter_note") || "Note cannot be empty");
      return;
    }
    onAddNote(newNoteText.trim());
    setNewNoteText("");
    setAddError("");
    setShowAddBox(false);
  };

  const handleUpdateSubmit = (noteId: string) => {
    if (!editingNoteText.trim()) return;
    onUpdateNote(noteId, editingNoteText.trim());
    setEditingNoteId(null);
    setEditingNoteText("");
  };

  return (
    <div className="flex flex-col gap-2 p-3 bg-white border border-slate-200/90 rounded-xl shadow-2xs">
      {/* Header: Title and '+ Add note' button */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5 text-xs font-semibold text-slate-700">
          <FileText className="w-3.5 h-3.5 text-slate-400" />
          <span>{t("notes")}</span>
          <span className="text-[11px] font-medium text-slate-400">
            ({notes.length})
          </span>
        </div>

        {!showAddBox && allowed && (
          <Button
            type="button"
            variant="none"
            size="sm"
            onClick={() => setShowAddBox(true)}
            className="flex items-center gap-1 h-7 px-2.5 text-xs font-semibold bg-blue-600 hover:bg-blue-700 text-white rounded-lg shadow-2xs transition-all cursor-pointer"
          >
            <Plus className="w-3.5 h-3.5 text-white" />
            <span>{t("add")}</span>
          </Button>
        )}
      </div>

      {/* Inline Add Note Input */}
      {showAddBox && (
        <div className="flex flex-col gap-2 p-2.5 bg-slate-50 border border-slate-200 rounded-lg mt-1">
          <textarea
            autoFocus
            rows={2}
            value={newNoteText}
            onChange={(e) => {
              setNewNoteText(e.target.value);
              if (addError) setAddError("");
            }}
            placeholder={t("type_note_here")}
            className="w-full text-xs font-normal text-slate-800 p-2 bg-white border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500"
          />
          {addError && <ErrorMessage className="text-xs">{addError}</ErrorMessage>}
          <div className="flex items-center justify-end gap-1.5">
            <Button
              type="button"
              variant="none"
              size="sm"
              onClick={() => {
                setShowAddBox(false);
                setNewNoteText("");
                setAddError("");
              }}
              className="h-7 px-2.5 text-xs text-slate-600 hover:bg-slate-200/60 rounded-md"
            >
              {t("cancel")}
            </Button>
            <Button
              type="button"
              variant="primary"
              size="sm"
              onClick={handleAddSubmit}
              className="h-7 px-3 text-xs font-semibold rounded-md shadow-2xs"
            >
              {t("add")}
            </Button>
          </div>
        </div>
      )}

      {/* Notes List */}
      <div className="flex flex-col gap-2 mt-1 max-h-56 overflow-y-auto pr-1">
        {notes.length === 0 && !showAddBox && (
          <span className="text-xs text-slate-400 italic py-1">
            {t("no_notes_recorded")}
          </span>
        )}

        {notes.map((item) => (
          <div
            key={item.noteId}
            className="p-2.5 bg-slate-50/70 border border-slate-100 rounded-lg text-xs flex flex-col gap-1.5"
          >
            <div className="flex items-center justify-between text-[11px] text-slate-400">
              <div className="flex items-center gap-1 truncate">
                <span>{dateFormater(new Date(item.createdAt))}</span>
                <span>•</span>
                <span className="font-medium text-slate-600 truncate">
                  {item.createdByUser?.firstName} {item.createdByUser?.lastName}
                </span>
                {item.updatedByUser && (
                  <span className="text-slate-400 truncate">
                    ({t("edited")})
                  </span>
                )}
              </div>

              {allowed && (
                <div className="flex items-center gap-1 shrink-0">
                  {editingNoteId !== item.noteId ? (
                    <>
                      <button
                        type="button"
                        onClick={() => {
                          setEditingNoteId(item.noteId);
                          setEditingNoteText(item.note);
                        }}
                        className="p-1 text-slate-400 hover:text-slate-600 rounded transition-colors"
                      >
                        <Edit2 className="w-3 h-3" />
                      </button>
                      <button
                        type="button"
                        onClick={() => setDeleteNoteId(item.noteId)}
                        className="p-1 text-slate-400 hover:text-red-600 rounded transition-colors"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        type="button"
                        onClick={() => {
                          setEditingNoteId(null);
                          setEditingNoteText("");
                        }}
                        className="p-1 text-slate-400 hover:text-slate-600 rounded"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleUpdateSubmit(item.noteId)}
                        className="p-1 text-emerald-600 hover:text-emerald-700 rounded"
                      >
                        <Check className="w-3.5 h-3.5" />
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>

            {editingNoteId === item.noteId ? (
              <div className="flex flex-col gap-1.5 mt-1">
                <textarea
                  autoFocus
                  rows={2}
                  value={editingNoteText}
                  onChange={(e) => setEditingNoteText(e.target.value)}
                  className="w-full text-xs font-normal text-slate-800 p-2 bg-white border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500"
                />
              </div>
            ) : (
              <p className="text-xs text-slate-700 whitespace-pre-wrap leading-relaxed">
                {item.note}
              </p>
            )}
          </div>
        ))}
      </div>

      {/* Delete Note Confirmation Modal */}
      <Dialog
        isOpen={Boolean(deleteNoteId)}
        onClose={() => setDeleteNoteId(null)}
        modalClass="rounded-xl max-w-sm p-5"
      >
        <div className="flex flex-col items-center text-center gap-3">
          <img src={TrashCan} className="w-12 h-12" alt="Trash" />
          <h3 className="font-bold text-slate-800 text-sm">
            {t("delete_note_confirm_msg")}
          </h3>
          <div className="flex items-center gap-2 mt-2 w-full">
            <Button
              type="button"
              variant="outline"
              onClick={() => setDeleteNoteId(null)}
              className="flex-1 text-xs"
            >
              {t("cancel")}
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={() => {
                if (deleteNoteId) {
                  onDeleteNote(deleteNoteId);
                  setDeleteNoteId(null);
                }
              }}
              className="flex-1 text-xs"
            >
              {t("delete")}
            </Button>
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default TaskNotesSection;
